import { useState, type ReactNode } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { Route as rootRoute } from './__root.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Label } from '../components/ui/Label.js';
import { ApiError, requestPasswordReset } from '../lib/api.js';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

/** Brand chrome shared with the signup / reset pages. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3 justify-center">
          <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center">
            <span className="font-serif text-cream text-lg leading-none">A</span>
          </div>
          <div className="font-serif text-2xl tracking-tighter2 text-ink">Admitly</div>
        </div>
        <div className="bg-white border border-line rounded-2xl shadow-card p-8">{children}</div>
      </div>
    </div>
  );
}

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      // Surface only rate-limiting; any other error must NOT reveal whether the
      // account exists — fall through to the same success state.
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests just now — please try again in a minute.');
      } else {
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">Check your inbox</h1>
          <p className="text-sm text-muted mb-6">
            If that email is registered, we&rsquo;ve sent a link to reset your password. It
            expires in one hour.
          </p>
          <Link to="/login">
            <Button variant="secondary" className="w-full">
              Back to login
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-serif text-2xl text-ink tracking-tight mb-1">Forgot your password?</h1>
      <p className="text-sm text-muted mb-6">
        Enter your email and we&rsquo;ll send you a link to set a new one.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="forgot-email">Email</Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@agency.com.au"
          />
        </div>

        {error && (
          <div className="rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <Button type="submit" size="lg" loading={submitting} className="w-full mt-2">
          Send reset link
        </Button>
      </form>

      <div className="mt-6 text-center text-xs text-muted">
        <Link to="/login" className="text-navy hover:underline">
          Back to login
        </Link>
      </div>
    </Shell>
  );
}
